import React, { useState, useRef, useEffect } from 'react'
import { Minimize2, GripVertical, Sparkles, MessageSquare } from 'lucide-react'
import { useCase } from '../../../../hooks/useCase'
import GuidedAISidebar from '../summary/GuidedAISidebar'
import { ActivityFeedPanel } from '../AgentStream'
import feneroLogo from '../../../../assets/logo-orange.svg'
import styles from './FloatingCaseIntelligence.module.css'

interface FloatingCaseIntelligenceProps {
  caseId: string | null
}

type PanelState = 'minimized' | 'expanded'
type PanelTab = 'stream' | 'guidance'

const FloatingCaseIntelligence: React.FC<FloatingCaseIntelligenceProps> = ({ caseId }) => {
  // Calculate 2/3 of viewport height
  const defaultHeight = Math.floor(window.innerHeight * 0.66)

  const [panelState, setPanelState] = useState<PanelState>('minimized')
  const [activeTab, setActiveTab] = useState<PanelTab>('stream')
  const [position, setPosition] = useState({
    x: window.innerWidth - 540,
    y: Math.max(80, window.innerHeight - defaultHeight - 40)
  })
  const [size, setSize] = useState({ width: 480, height: defaultHeight })
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const panelRef = useRef<HTMLDivElement>(null)

  // Get case data from Firestore to check processing status
  const { caseData } = useCase(caseId || undefined)

  // Check if document scanning is in progress
  const isProcessing = caseData?.documentScan?.status === 'scanning'

  // Don't render if no case is selected
  if (!caseId) {
    return null
  }

  // Handle drag start
  const handleMouseDown = (e: React.MouseEvent) => {
    if (panelState !== 'expanded') return
    
    setIsDragging(true)
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    })
  }

  // Handle mouse move (drag)
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return

      const newX = e.clientX - dragOffset.x
      const newY = e.clientY - dragOffset.y

      // Constrain to viewport
      const maxX = window.innerWidth - size.width
      const maxY = window.innerHeight - size.height

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      })
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, dragOffset, size])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && panelState === 'expanded') {
        setPanelState('minimized')
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [panelState])

  // Minimized bubble
  if (panelState === 'minimized') {
    return (
      <button
        className={styles.minimizedBubble}
        onClick={() => setPanelState('expanded')}
        title="Open Case Intelligence"
      >
        <img src={feneroLogo} alt="Fenero" className={styles.bubbleLogoSmall} />
        <span className={styles.bubbleText}>AI</span>
      </button>
    )
  }

  // Expanded panel
  return (
    <div
      ref={panelRef}
      className={`${styles.floatingPanel} ${isDragging ? styles.dragging : ''}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: `${size.height}px`
      }}
    >
      {/* Header - Draggable */}
      <div
        className={styles.header}
        onMouseDown={handleMouseDown}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div className={styles.titleRow}>
          <GripVertical size={16} className={styles.gripIcon} />
          <img src={feneroLogo} alt="Fenero" className={styles.headerLogo} />
          <h2 className={styles.title}>Case Intelligence</h2>
          {isProcessing && (
            <span className={styles.liveIndicator}>
              <span className={styles.liveDot} />
              Live
            </span>
          )}
        </div>
        <div className={styles.controls}>
          <button
            className={styles.controlButton}
            onClick={() => setPanelState('minimized')}
            title="Minimize"
          >
            <Minimize2 size={16} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'stream' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('stream')}
        >
          <Sparkles size={14} />
          <span>Agent Activity</span>
          {isProcessing && <span className={styles.processingDot} />}
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'guidance' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('guidance')}
        >
          <MessageSquare size={14} />
          <span>Guidance</span>
        </button>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {activeTab === 'stream' ? (
          <ActivityFeedPanel
            caseId={caseId}
            maxHeight={`${size.height - 110}px`}
            showHeader={false}
          />
        ) : (
          <GuidedAISidebar caseId={caseId} />
        )}
      </div>
    </div>
  )
}

export default FloatingCaseIntelligence

